// @flow

import React, { Component, type Element, Fragment } from "react";
import { noop, get, isEqual } from "lodash";
import type {
  RaceCardRunner,
  RaceCardBettingInterest,
  RaceTypeCodeEnum
} from "@tvg/types/Race";
import type { RacePanelLink } from "@fdr/types/Race";
import type { BinaryFn, TernaryFn, UnaryFn } from "@tvg/types/Functional";
import type { UserOptedInPromos } from "@tvg/types/User";

import RunnerNumber from "../../_atoms/RunnerNumber";
import {
  RunnerWrapper,
  JockeySilk,
  JockeySilkContainer,
  RunnerInfoContainer,
  RunnerContainer,
  HorseDescription,
  DriverDescription,
  OddsContainer,
  OddInfo,
  RunnerLineWrapper,
  RunnerInfo,
  HorseName,
  ScratchedTag
} from "./styled-components";
import Tag from "../../_atoms/Tag";

type Props = {
  raceTypeCode: RaceTypeCodeEnum,
  selections: boolean[],
  numColumns: number,
  redirectToRaceCard: boolean,
  raceUrl: string,
  race: ?RacePanelLink,
  hasHover: boolean,
  onSelectAllRunnerEntries: UnaryFn<string, void>,
  runner: RaceCardRunner,
  bettingInterest: RaceCardBettingInterest,
  isLeg: boolean,
  leg: number,
  isLogged: boolean,
  isCoupled: boolean,
  expandedInfo: BinaryFn<number, number, Element<*> | void>,
  updatesRL: boolean,
  changeUpdate: TernaryFn<string, number, boolean, void>,
  onRunnerClick: TernaryFn<
    RacePanelLink,
    string,
    string | UserOptedInPromos,
    void
  >,
  isOpen: boolean,
  isNotLastRunner: boolean,
  isOpeningApp: boolean,
  showSilks: boolean
};

export default class RunnerLine extends Component<Props> {
  static defaultProps = {
    raceTypeCode: "T",
    isOpen: false,
    selections: [false],
    numColumns: 1,
    redirectToRaceCard: false,
    raceUrl: "/",
    race: null,
    hasHover: false,
    onSelectAllRunnerEntries: noop,
    runner: null,
    bettingInterest: null,
    isLeg: false,
    leg: -1,
    isLogged: false,
    isCoupled: false,
    expandedInfo: noop,
    updatesRL: false,
    onRunnerClick: noop,
    changeUpdate: noop,
    isNotLastRunner: true,
    isOpeningApp: false,
    showSilks: false
  };

  shouldComponentUpdate(nextProps: Props) {
    return (
      this.props.isOpen !== nextProps.isOpen ||
      this.props.numColumns !== nextProps.numColumns ||
      this.props.isLogged !== nextProps.isLogged ||
      !isEqual(this.props.bettingInterest, nextProps.bettingInterest) ||
      !isEqual(this.props.selections, nextProps.selections) ||
      this.props.showSilks !== nextProps.showSilks
    );
  }

  onExpand = () => {
    this.props.changeUpdate(
      get(this.props, "runner.runnerId"),
      this.props.leg,
      this.props.isOpen
    );
  };

  formatOdd = (): string =>
    !get(this.props, "bettingInterest.currentOdds.denominator")
      ? `${get(this.props, "bettingInterest.currentOdds.numerator")}/1`
      : `${get(this.props, "bettingInterest.currentOdds.numerator", 0)}/${get(
          this.props,
          "bettingInterest.currentOdds.denominator"
        )}`;

  renderOdds = () => (
    <Fragment>
      <OddsContainer>
        <OddInfo>
          {this.props.isCoupled && (
            <Tag tagType="CoupledTag" name="COUPLED" size={16} />
          )}
          {get(this.props, "bettingInterest.isFavorite") && (
            <Tag size={16} name="FAV" tagType="Fav" />
          )}
          {this.formatOdd()}
        </OddInfo>
      </OddsContainer>
    </Fragment>
  );

  renderBetSection = (isScratched: boolean) =>
    !isScratched ? (
      this.renderOdds()
    ) : (
      <ScratchedTag tagType="CoupledTag" name="scratched" size={17} />
    );

  render() {
    const hasTimeformInfo =
      this.props.showSilks && !!get(this.props, "runner.timeform");
    const isScratched = get(this.props, "runner.scratched");
    return (
      <RunnerLineWrapper
        onClick={(e) => {
          if (this.props.isOpeningApp) {
            e.preventDefault();
            e.stopPropagation();
          }
          this.props.onRunnerClick(
            get(this.props, "race"),
            this.props.raceUrl,
            "Runner"
          );
        }}
        to={get(this.props, "raceUrl", "/")}
      >
        <RunnerContainer>
          <RunnerWrapper
            key={`runner-${get(this.props, "runner.runnerId")}`}
            data-qa-label={`${get(this.props, "runner.runnerId")}-runnerLine${
              `${get(this.props, "runner.runnerId", "0")}`.match(/[a-zA-Z]/i)
                ? "-coupled"
                : ""
            }${isScratched ? "-scratched" : ""}`}
            scratched={isScratched}
          >
            <RunnerInfo>
              <JockeySilkContainer hasTimeformInfo={hasTimeformInfo}>
                {hasTimeformInfo && (
                  <JockeySilk
                    src={
                      get(this.props, "runner.timeform.silkUrlSvg", "") ||
                      get(this.props, "runner.timeform.silkUrl", "")
                    }
                  />
                )}
                <RunnerNumber
                  raceTypeCode={this.props.raceTypeCode}
                  runnerId={get(this.props, "runner.runnerId")}
                  hasTimeformInfo={hasTimeformInfo}
                  isBigSize={!hasTimeformInfo}
                />
              </JockeySilkContainer>
              <RunnerInfoContainer isNotLastRunner={this.props.isNotLastRunner}>
                <HorseDescription>
                  <HorseName>{get(this.props, "runner.horseName")}</HorseName>
                  {this.renderBetSection(isScratched)}
                </HorseDescription>
                <DriverDescription>
                  <span>
                    {this.props.raceTypeCode === "H" ? "D" : "J"}:{" "}
                    {get(this.props, "runner.jockey")}
                  </span>
                </DriverDescription>
              </RunnerInfoContainer>
            </RunnerInfo>
          </RunnerWrapper>
        </RunnerContainer>
      </RunnerLineWrapper>
    );
  }
}
